import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X } from 'lucide-react';

export const Header = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About Us' },
    { path: '/partner', label: 'Scale With Us' },
  ];

  return (
    <header
      data-testid="header"
      style={{
        width: '100%',
        height: '72px',
        background: 'linear-gradient(180deg, #fafbfc 0%, #f7f8fa 100%)',
        borderBottom: '1px solid rgba(15,33,55,0.07)',
        display: 'flex',
        alignItems: 'center',
        padding: '0 56px',
        position: 'relative',
      }}
    >
      {/* Logo — left */}
      <Link to="/" className="shrink-0" data-testid="header-logo-link" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none', position: 'relative', zIndex: 1 }}>
        <img src="/verline-v-icon.png" alt="Verline icon" data-testid="header-v-icon" style={{ height: '126px' }} />
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <span
            data-testid="header-logo-text"
            style={{
              color: '#0a192f',
              fontWeight: 800,
              fontSize: '32px',
              letterSpacing: '0.2em',
              lineHeight: 1,
              fontFamily: "'General Sans', sans-serif",
            }}
          >
            VERLINE
          </span>
          <span
            style={{
              color: '#94a3b8',
              fontWeight: 500,
              fontSize: '10.5px',
              letterSpacing: '0.06em',
              lineHeight: 1,
              marginTop: '5px',
            }}
          >
            Marketplace Growth & Distribution
          </span>
        </div>
      </Link>

      {/* Nav — centered */}
      <nav className="hidden lg:flex items-center absolute left-1/2" style={{ transform: 'translateX(-50%)', gap: '8px' }} data-testid="header-nav">
        {navLinks.map((link) => (
          <Link
            key={link.path}
            to={link.path}
            data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
            style={{
              color: isActive(link.path) ? '#0a192f' : '#4b5563',
              fontSize: '14.5px',
              fontWeight: isActive(link.path) ? 600 : 500,
              padding: '8px 18px',
              borderRadius: '8px',
              transition: 'color 0.25s ease',
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              position: 'relative',
            }}
            onMouseEnter={e => {
              if (!isActive(link.path)) {
                e.currentTarget.style.color = '#2A8A6E';
              }
            }}
            onMouseLeave={e => {
              if (!isActive(link.path)) {
                e.currentTarget.style.color = '#4b5563';
              }
            }}
          >
            {link.label}
            {isActive(link.path) && (
              <span style={{
                position: 'absolute',
                bottom: '2px',
                left: '18px',
                right: '18px',
                height: '2px',
                backgroundColor: '#2A8A6E',
                borderRadius: '1px',
              }} />
            )}
          </Link>
        ))}
      </nav>

      {/* CTA — right */}
      <Link to="/partner" className="hidden lg:block ml-auto" style={{ position: 'relative', zIndex: 1 }}>
        <Button
          data-testid="header-cta-btn"
          style={{
            padding: '11px 28px',
            fontSize: '13.5px',
            fontWeight: 600,
            borderRadius: '10px',
            backgroundColor: '#2A8A6E',
            color: '#ffffff',
            border: 'none',
            boxShadow: '0 2px 8px rgba(42,138,110,0.25)',
            transition: 'all 0.25s ease',
            cursor: 'pointer',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = '#237a60';
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 4px 14px rgba(42,138,110,0.35)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = '#2A8A6E';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(42,138,110,0.25)';
          }}
        >
          Scale With Us ↗
        </Button>
      </Link>

      {/* Mobile Toggle */}
      <button
        className="lg:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors ml-auto"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        data-testid="mobile-menu-toggle"
        aria-label="Toggle menu"
      >
        {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </button>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <nav
          className="lg:hidden"
          data-testid="mobile-menu"
          style={{
            position: 'absolute',
            top: '71px',
            left: 0,
            right: 0,
            background: 'linear-gradient(180deg, #fafbfc 0%, #f7f8fa 100%)',
            borderBottom: '1px solid rgba(15,33,55,0.07)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
            padding: '12px 24px',
            zIndex: 50,
          }}
        >
          <div className="flex flex-col space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-link-${link.path.replace('/', '') || 'home'}`}
                onClick={() => setMobileMenuOpen(false)}
                className={`px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                  isActive(link.path)
                    ? 'text-navy-900 bg-teal-50'
                    : 'text-gray-500 hover:text-navy-900 hover:bg-gray-50'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link to="/partner" onClick={() => setMobileMenuOpen(false)}>
              <Button
                className="w-full mt-2 text-white rounded-lg py-2.5"
                style={{ backgroundColor: '#2A8A6E' }}
              >
                Scale With Us ↗
              </Button>
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
};
